import React, { useState } from 'react'

import { encodeToBase64, isAcceptedFormat } from '../../file-uploader-agnostic/utils'
import { useAjaxContext } from '../../../core/ajax'
import { DEFAULT_FILE_SIZE, API_ENDPOINT } from '../constants'

/**
 * @typedef {object} ErrorResponse
 * @property {string} mssg - El mensaje de error a mostrar al usuario.
 */

/**
 * @callback ChildrenRenderFunction
 * @param {object} state - El estado actual del componente.
 * @param {boolean} state.loading - Si es `true`, el archivo se está subiendo al servidor.
 * @returns {React.ReactNode} El contenido a renderizar (típicamente un botón).
 */

/**
 * @typedef {object} FileUploaderNotAssociatedProps
 * @property {object} [classes] - Objeto con los nombres de las clases CSS (no utilizado actualmente en el componente).
 * @property {string[]} accept - Un array de extensiones de archivo aceptadas (ej. `['.pdf', '.jpg', '.png']`).
 * @property {function(object): void} onSuccess - La función callback que se ejecuta cuando el archivo se sube correctamente. Recibe los datos de respuesta del servidor.
 * @property {function(ErrorResponse): void} onError - La función callback que se ejecuta cuando ocurre un error durante la carga.
 * @property {boolean} [disabled] - Si es `true`, el input de archivo estará deshabilitado.
 * @property {number} [maxSize=DEFAULT_FILE_SIZE] - El tamaño máximo permitido del archivo en bytes.
 * @property {string} [type='sin_informar'] - El tipo de documento que se está subiendo, usado para clasificar el archivo en el backend.
 * @property {string | number} [applicationId] - El ID de la solicitud asociada al documento (no utilizado actualmente en el payload).
 * @property {string | number} [budgetId] - El ID del presupuesto asociado al documento (opcional).
 * @property {ChildrenRenderFunction} children - Una función render prop que recibe el estado del componente y devuelve el UI a renderizar.
 * @property {string} id - Un identificador único para el input de archivo.
 */

/**
 * Un componente de carga de archivos que sube archivos al servidor sin asociarlos a una solicitud específica.
 * 
 * Similar a `FileUploader`, pero diseñado para subir documentos que no están vinculados directamente
 * a una solicitud (`save_event: false`). Valida el formato y tamaño del archivo, lo codifica en Base64
 * y lo envía a través de una petición POST al endpoint configurado.
 * 
 * Utiliza el patrón render prop a través de `children` para permitir una personalización
 * completa del UI del botón de carga.
 * 
 * **Nota:** Este componente requiere acceso al contexto de Ajax (`useAjaxContext`).
 * 
 * @component
 * @example
 * const MyUploadForm = () => {
 *   const handleSuccess = (response) => {
 *     console.log('Archivo subido con éxito:', response);
 *   };
 * 
 *   const handleError = (error) => {
 *     alert(`Error: ${error.mssg}`);
 *   };
 * 
 *   return (
 *     <FileUploaderNotAssociated
 *       id="temp-upload"
 *       accept={['.pdf', '.jpg']}
 *       maxSize={5000000} // 5 MB
 *       type="temporal"
 *       budgetId="12345"
 *       onSuccess={handleSuccess}
 *       onError={handleError}
 *     >
 *       {({ loading }) => (
 *         <button disabled={loading}>
 *           {loading ? 'Subiendo...' : 'Subir Archivo Temporal'}
 *         </button>
 *       )}
 *     </FileUploaderNotAssociated>
 *   );
 * };
 * 
 * @param {FileUploaderNotAssociatedProps} props - Las props del componente FileUploaderNotAssociated.
 */
export const FileUploaderNotAssociated = ({
  classes,
  accept,
  onSuccess,
  onError,
  disabled,
  maxSize = DEFAULT_FILE_SIZE,
  type = 'sin_informar',
  applicationId,
  budgetId,
  children,
  ...props
}) => {
  const { post } = useAjaxContext()
  const [loading, setLoading] = useState(false)

  const handleChangeUpload = async (e) => {
    setLoading(true)
    const file = e.target.files[0]
    if (file) {
      if (accept && accept.length > 0 && !isAcceptedFormat(file.name, accept)) {
        setLoading(false)
        return onError({ mssg: 'Archivo con formato no válido' })
      } else if (file.size > maxSize) {
        setLoading(false)
        return onError({ mssg: 'Excede el tamaño máximo' })
      }
      const mockErrorData = {
        mssg: 'Problema con la subida del fichero',
      }
      postDocument(file)
        .then(({ data }) => {
          setLoading(false)
          return onSuccess(data)
        })
        .catch(({ response }) => {
          setLoading(false)
          return onError(response ? response.data : mockErrorData)
        })
    }
  }

  const postDocument = async (file) => {
    const base64File = await encodeToBase64(file)
    const payload = {
      documento: base64File.split(',')[1],
      nombre: file.name.replace(/\.[^/.]+$/, ''),
      tipo: type,
      extension: file.type.split('/')[1],
      save_event: false,
    }
    if (budgetId) {
      payload['presupuesto_id'] = budgetId.toString()
    }
    return post(API_ENDPOINT, payload, { timeout: 0 })
  }

  return (
    <div>
      <input
        id={`file-upload-${props.id}`}
        type="file"
        style={{ display: 'none' }}
        accept={accept.join(',')}
        onChange={handleChangeUpload}
        disabled={disabled}
      />
      <label htmlFor={`file-upload-${props.id}`}>{children({ loading })}</label>
    </div>
  )
}
